/**
 * Shared score/severity/section primitives used across EssayGrader result panels.
 * Extracted from EssayGrader.js to keep the page file under ~300 lines and let
 * each result section be tested in isolation.
 */
import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { ChevronDown, ChevronUp } from 'lucide-react';

export function ScoreBadge({ score, max = 100, label }) {
  const pct = max > 0 ? (score / max) * 100 : 0;
  const color = pct >= 80 ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : pct >= 60 ? 'text-amber-700 bg-amber-50 border-amber-200'
    : 'text-red-700 bg-red-50 border-red-200';
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-md border text-sm font-semibold ${color}`} data-testid={`score-badge-${label}`}>
      {score}{max !== 100 && `/${max}`}
    </span>
  );
}

export function SeverityDot({ level }) {
  const c = level === 'high' ? 'bg-red-500' : level === 'medium' ? 'bg-amber-500' : 'bg-emerald-500';
  return <span className={`inline-block w-2 h-2 rounded-full ${c} mr-2`} />;
}

export function CollapsibleSection({ title, icon: Icon, children, defaultOpen = false, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="border border-stone-200 bg-white overflow-hidden" data-testid={testId}>
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-stone-50 transition-colors"
        onClick={() => setOpen(!open)}
        data-testid={`${testId}-toggle`}
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="h-5 w-5 text-stone-600" />}
          <span className="font-semibold text-stone-800">{title}</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-stone-400" /> : <ChevronDown className="h-4 w-4 text-stone-400" />}
      </button>
      {open && <div className="px-5 pb-5 border-t border-stone-100">{children}</div>}
    </Card>
  );
}
